import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchById } from "services/assets.service";
import { Asset } from "types/Asset";

interface AssetContainerState {
  isLoading: boolean;
  isError: boolean;
  errMsg: string;
  assetDetails: Asset | null;
  // The asset id of the latest in-flight request. Responses (success or error)
  // only apply if they still match it, so a stale response for an asset we've
  // navigated away from can't overwrite the current one or surface a wrong error.
  requestedAssetId: string | null;
}

const initialState: AssetContainerState = {
  isLoading: true,
  isError: false,
  errMsg: "",
  assetDetails: null,
  requestedAssetId: null,
};

// Create the slice
const assetContainerSlice = createSlice({
  name: "assetcontainer",
  initialState,
  reducers: {
    setDestroy() {
      return initialState;
    },
    setLoading(state: AssetContainerState, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    // Marks the start of a fetch: records which asset is requested, shows
    // loading, and clears any prior error so a stale error can't block the new
    // asset.
    setRequest(state: AssetContainerState, action: PayloadAction<string>) {
      state.requestedAssetId = action.payload;
      state.isLoading = true;
      state.isError = false;
      state.errMsg = "";
    },
    setError(
      state: AssetContainerState,
      action: PayloadAction<{ id: string; error: string }>
    ) {
      if (state.requestedAssetId !== action.payload.id) {
        return;
      }
      state.isLoading = false;
      state.isError = true;
      state.errMsg = action.payload.error;
    },
    setAssetContainer(
      state: AssetContainerState,
      action: PayloadAction<{ id: string; data: any }>
    ) {
      if (state.requestedAssetId !== action.payload.id) {
        return;
      }
      state.assetDetails = action.payload.data;
      state.isLoading = false;
      state.isError = false;
      state.errMsg = "";
    },
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = assetContainerSlice;

// Extract and export each action creator by name
export const { setDestroy, setLoading, setRequest, setError, setAssetContainer } =
  actions;

// Export the reducer, either as a default or named export
export default reducer;

// Define the type for the dispatch function
export const fetchAssetDetails =
  ({ companyId, id }: { companyId: string; id: string }) =>
  async (dispatch: any) => {
    dispatch(setRequest(id));
    try {
      const data = await fetchById(companyId, id);
      dispatch(setAssetContainer({ id, data }));
    } catch (e) {
      const error = "not_fetch";
      dispatch(setError({ id, error }));
      return false;
    }
  };
